import { createContext } from 'react'
import { useState, useEffect, useRef, useImperativeHandle, RefObject } from 'react'
import { createContainer } from 'unstated-next'
import * as api from '../../../infrastructure/api'
import { hasScopeOnClaim, hasDealClaim } from '../../../infrastructure/signIn/userContext'
import { Claims } from '../../../infrastructure/signIn/models'
import guid, { Guid } from '../../../infrastructure/guid'
import {
    Vessel, VesselUser, Counterparty, Jetty, VesselProduct, PriceIndex, CompanyName, ProductCodes, Site, Stock, PaymentTerm,
    SitePurchaseMovement, ComplementaryVesselFields, PricingNeed, PurchaseMovement, PricingDensitysWithDefault,
    VesselMeanOfTransportation, VesselReferenceVehicle, activateVesselReferenceIdMasterDataFeature, PurchaseMovementType
} from '../vesselModels'
import { CostingInformation, RegulatedPricePeriod } from '../vesselCostingInformationModels'
import { hasFeature } from '../../../infrastructure/feature'
import { snackbars } from '../../../infrastructure/snackbars'
import { Deal } from '../../deals/dealModels'
import { t } from '../../../infrastructure/i18nextHelper'
import { usePrevious } from '../../../infrastructure/customHooks'
import { useVesselFormErrors } from './vesselFormErrors'
import { dealApi } from '../../deals/dealApis'
import { PricingPeriodRule } from '../../administration/masterData/pricingPeriodRule'

type OpenCloseDialogRef = { open: (id?: Guid, productId?: Guid) => void, close: () => void }
let dialogRef: RefObject<OpenCloseDialogRef> | null = null

export let vesselDialog = {
    open: (id?, productId?) => dialogRef?.current?.open(id, productId),
    close: () => dialogRef?.current?.close(),
}

function useVesselEdit(initialState) {
    let dialog = VesselEditDialogContainer.useContainer()
    let [state, setState] = useState<Vessel>(initialState)
    let [canEdit, setCanEdit] = useState<boolean>(false)
    let [vesselUsers, setVesselUsers] = useState<VesselUser[]>([])
    let [counterpartys, setCounterpartys] = useState<Counterparty[]>([])
    let [sites, setSites] = useState<Site[]>([])
    let [companys, setCompanys] = useState<CompanyName>({})
    let [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>([])
    let [products, setProducts] = useState<ProductCodes>({})
    let [jettys, setJettys] = useState<Jetty[]>([])
    let [priceIndexs, setPriceIndexs] = useState<PriceIndex[]>([])
    let [pricingPeriodRules, setPricingPeriodRules] = useState<PricingPeriodRule[]>([])
    let [pricingDensitys, setPricingDensitys] = useState<PricingDensitysWithDefault>({ defaultPricingDensity: null, pricingDensitys: [] })
    let [purchaseMovementsByVesselProductId, setPurchaseMovementsByVesselProductId] = useState<{ [productId: string]: PurchaseMovement[] }>({})
    let [costingInformationByVesselProductId, setCostingInformationByVesselProductId] = useState<{ [productId: string]: CostingInformation }>({})
    let [vesselFieldsCatalog, setVesselFieldsCatalog] = useState<string[]>([])
    let [movementStock, setMovementStock] = useState<Stock[]>([])
    let [activeProductId, setActiveProductId] = useState<Guid | null>(null)
    let [pricingNeeds, setPricingNeeds] = useState<{ [sitePurchaseMovementId: string]: number | null }>({})
    let [fixedPriceByVesselProductId, setFixedPriceByVesselProductId] = useState<{ [purchaseMovementId: string]: number | null }>({})
    let [regulatedPricePeriods, setRegulatedPricePeriods] = useState<RegulatedPricePeriod[]>([])
    let [transporters, setTransporters] = useState<{ [company: string]: Counterparty[] }>({})
    let [salesCounterpartys, setSalesCounterpartys] = useState<{ [company: string]: Counterparty[] }>({})
    let [openAssignDealPopup, setOpenAssignDealPopup] = useState<boolean>(false)
    let [assignableDealsFromMovement, setAssignableDealsFromMovement] = useState<Deal[] | null>(null)
    let [dealIdToAssign, setDealIdToAssign] = useState<Guid | null>(null)
    let [isConfirmDealOpen, setIsConfirmDealOpen] = useState<boolean>(false)
    let [sitePurchaseMovementWithAssignedDeal, setSitePurchaseMovementWithAssignedDeal] = useState<SitePurchaseMovementWithAssignedDealInfo | null>(null)
    let [canCreatePurchaseOrder, setCanCreatePurchaseOrder] = useState<boolean>(false)
    let [canEditPurchaseOrder, setCanEditPurchaseOrder] = useState<boolean>(false)
    let [referenceVehicles, setReferenceVehicles] = useState<VesselReferenceVehicle[]>([])
    let [quotationCalendars, setQuotationCalendars] = useState<{ [priceIndexCode: string]: string[] }>({})
    let [unassignDealConfirmDialogOpen, setUnassignDealConfirmDialogOpen] = useState<boolean>(false)
    let [unassignDealContext, setUnassignDealContext] = useState<{ dealId: Guid | null; movementId: Guid | null }>({ dealId: null, movementId: null })

    let vesselState = state
    let formErrorsContext = createContext({ vesselState })
    let formErrors = useVesselFormErrors(formErrorsContext)
    let isReferenceIdMasterDataActive = hasFeature(activateVesselReferenceIdMasterDataFeature)

    type SitePurchaseMovementWithAssignedDealInfo = {
        purchaseMovementId: Guid
        sitePurchaseMovementId: Guid
        productId: Guid
    }

    useEffect(() => {
        if (state?.jettyCode && state?.completionOfDischargeDate)
            loadRegulatedPricePeriods(state.jettyCode, state.completionOfDischargeDate)
    }, [state?.completionOfDischargeDate])

    useEffect(() => {
        if (state?.jettyCode && !jettys.find(x => x.code == state.jettyCode)) {
            setJettys([{ code: state.jettyCode, defaultSite: '', country: '', defaultMeanOfTransportation: 'Ship' }])
        }
    }, [state?.id])

    useEffect(() => {
        let loadCalendars = async () => {
            let calendars: { [priceIndexCode: string]: string[] } = {}
            for (const priceIndex of priceIndexs) {
                calendars[priceIndex.medecoCode] = await getQuotationCalendar(priceIndex.medecoCode)
            }
            setQuotationCalendars(calendars)
        }
        loadCalendars()
    }, [priceIndexs])

    useEffect(() => {
        if (!isReferenceIdMasterDataActive) {
            setReferenceVehicles([])
            return
        }

        let isCurrent = true
        api.get<VesselReferenceVehicle[]>('vessel/referenceVehicles').then(vehicles => {
            if (isCurrent) setReferenceVehicles(vehicles)
        })

        return () => { isCurrent = false }
    }, [isReferenceIdMasterDataActive])

    let getQuotationCalendar = async (medecoCode: string) => {
        let quotationCalendar = await api.get<{ id: string, office: string, date: string }[]>(`pricing/priceIndex/${medecoCode}/quotationCalendar`)
        return quotationCalendar.map(x => x.date)
    }

    useEffect(() => {
        updateSites(state?.jettyCode)
        updateCounterpartys(state?.jettyCode)

        if (!state?.jettyCode) return

        loadUsers(state.jettyCode)
        loadVesselFieldCatalog(state.jettyCode)
        loadProducts(state.jettyCode)
    }, [state?.jettyCode])

    let updateSites = async (jettyCode: string | null) => {
        if (!jettyCode) {
            setSites([])
            setMovementStock([])
        }
        else {
            let sites = api.get<Site[]>(`vessel/site?jettyCode=${jettyCode}`)
            let stock = api.get<Stock[]>(`vessel/jetty/${jettyCode}/stock`)
            setSites(await sites)
            setMovementStock(await stock)
        }
    }

    let updateCounterpartys = async (jettyCode: string | null) => {
        if (!jettyCode) setCounterpartys([])
        else {
            let counterpartys = await api.get<Counterparty[]>(`vessel/counterparty/${jettyCode}`)
            setCounterpartys(counterpartys.filter(x => !x.isInternal))
        }
    }

    let changeProduct = (product: VesselProduct) => {
        if (state === null) return
        state.products[state.products.map(x => x.id).indexOf(product.id)] = { ...product }
        setState({ ...state, products: [...state.products] })
    }

    let getCounterpartyId = (value: Guid | null) =>
        counterpartys.find(x => x.id === value)?.id ?? null

    let changeCounterparty = (value: Guid | null) => {
        if (!state) return
        setState({ ...state, counterpartySupplierId: getCounterpartyId(value) })
    }

    let changePurchaseMovementCounterparty = (
        vesselProductId: Guid,
        purchaseMovement: PurchaseMovement,
        value: Guid | null
    ) => {
        let counterpartyId = transporters[purchaseMovement.companyCode]?.find(x => x.id === value)?.id ?? null
        setPurchaseMovement(vesselProductId, {
            ...purchaseMovement,
            sites: purchaseMovement.sites.map(site => ({ ...site, counterpartyId }))
        })
    }

    const getProductCodeProductIds = () => {
        return Object.fromEntries(Object.entries(products).map(([productId, productCode]) => [productCode, productId]))
    }

    const addProducts = async (productIds: string[]) => {
        if (!state?.jettyCode) return
        if (!productIds.length) return
        const fetchedProducts = await Promise.all(
            productIds.map(productId => api.get<VesselProduct>(`vessel/${state.id}/product/${productId}?jetty=${state.jettyCode}`))
        )

        const newProducts = fetchedProducts
            .filter(product =>
                !state.products.some(x => x.productId === product.productId)
            )
            .map(product => ({
                ...product,
                canRemove: true,
                isActive: true
            }))

        if (!newProducts.length) return

        updateVesselProducts(newProducts)
        updateCostingInformations(newProducts)

        setState({
            ...state,
            products: [...state.products]
        })
    }

    let addAllPurchaseMovements = async (vesselProductId: string, type: PurchaseMovementType = 'FormulaPrice') => {
        let product = state?.products.find(x => x.id === vesselProductId)
            ?? state?.products.find(x => x.productId === vesselProductId)
        if (!product) return

        let existing = purchaseMovementsByVesselProductId[product.id] ?? []
        let productStocks = movementStock.filter(x => x.productId === product.productId)
        let stocksToAdd = productStocks
            .filter((stock, index, stocks) => stocks.findIndex(x => x.company === stock.company && x.dutyStatus === stock.dutyStatus) === index)
            .filter(stock => !existing.some(x => x.companyCode === stock.company && x.dutyStatus === stock.dutyStatus && x.type === type))
        if (!stocksToAdd.length) return

        await loadCounterpartys(stocksToAdd.map(x => x.company).distinct())

        let purchaseMovements = stocksToAdd.map(stock => {
            let availableSites = productStocks
                .filter(x => x.company === stock.company && x.dutyStatus === stock.dutyStatus)
                .map(x => x.site)
            let defaultSite = jettys.find(x => x.code === state.jettyCode)?.defaultSite
            if (!defaultSite || !availableSites.includes(defaultSite))
                defaultSite = availableSites[0]

            let site: SitePurchaseMovement = {
                id: guid.createNew(),
                isDefault: false,
                volume: null,
                volumeOverride: false,
                quantity: null,
                quantityOverride: false,
                siteCode: defaultSite,
                dealId: null,
                dealNumber: null,
                movementId: null,
                availabilityDate: state.availabilityDate,
                availabilityDateOverwritten: false,
                releaseDate: null,
                releaseDateOverwritten: false,
                aseReference: null,
                premium: null,
                fixedDate: null,
                fixedPrice: null,
                deemedDate: null,
                pricingFrom: product.pricingStartDate,
                pricingFromOverwritten: false,
                pricingTo: product.pricingEndDate,
                pricingToOverwritten: false,
                contractualDensity: null,
                contractualDensityOverwritten: false,
                purchaseOrder: null,
                transporterId: null,
                transferDestinationSite: null,
                movementType: 1,
                formulaType: null,
                pricingPeriodRuleId: null,
                counterpartyId: null
            }
            let purchaseMovement: PurchaseMovement = {
                id: guid.createNew(),
                companyCode: stock.company,
                dutyStatus: stock.dutyStatus,
                type,
                percentageOfNominated: null,
                inAlert: null,
                nominatedQuantity: null,
                remainingQuantity: null,
                totalDispatchedQuantity: null,
                deadlineDate: null,
                purchaseContract: null,
                sites: [type === 'FixedPrice' ? applyFixedPrice(product.id, site) : site],
                order: 0
            }
            return purchaseMovement
        })

        setPurchaseMovementsByVesselProductId(previous => {
            let existing = previous[product.id] ?? []
            let newMovements = purchaseMovements.filter(movement => !existing.some(x =>
                x.companyCode === movement.companyCode && x.dutyStatus === movement.dutyStatus && x.type === movement.type))
            if (!newMovements.length) return previous
            return { ...previous, [product.id]: [...existing, ...newMovements] }
        })
    }

    let updateProductVolume = (vesselProductId: Guid, field: 'billOfLading' | 'outturn' | 'reception' | 'company1Outturn' | 'company1Reception' | 'company2Outturn' | 'company2Reception', volume: number | null) => {
        setState(previous => {
            if (!previous?.products.some(x => x.id === vesselProductId)) return previous
            return {
                ...previous,
                products: previous.products.map(product => product.id === vesselProductId
                    ? { ...product, [field]: { ...product[field], volume, volumeOverwritten: volume != null } }
                    : product)
            }
        })
    }

    let updatePurchaseMovementVolume = (vesselProductId: Guid, purchaseMovementId: Guid, sitePurchaseMovementId: Guid, volume: number | null) => {
        setPurchaseMovementsByVesselProductId(previous => {
            let movements = previous[vesselProductId]
            if (!movements?.some(x => x.id === purchaseMovementId && x.sites.some(site => site.id === sitePurchaseMovementId))) return previous
            return {
                ...previous,
                [vesselProductId]: movements.map(movement => movement.id === purchaseMovementId
                    ? {
                        ...movement, sites: movement.sites.map(site => site.id === sitePurchaseMovementId
                            ? { ...site, volume, volumeOverride: volume != null }
                            : site)
                    }
                    : movement)
            }
        })
    }

    let setPurchaseMovement = (vesselProductId: Guid, purchaseMovement: PurchaseMovement) =>
        setPurchaseMovements(vesselProductId, [purchaseMovement])

    let setPurchaseMovements = (vesselProductId: Guid, purchaseMovements: PurchaseMovement[]) =>
        setPurchaseMovementsProducts([[vesselProductId, purchaseMovements]])

    let setPurchaseMovementsProducts = (purchaseMovementsProducts: [Guid, PurchaseMovement[]][]) => {
        let newPurchaseMovementsByVesselProductId = purchaseMovementsByVesselProductId
        for (let purchaseMovementsProduct of purchaseMovementsProducts) {
            let productId = purchaseMovementsProduct[0]
            let purchaseMovements = purchaseMovementsProduct[1]
            let existing = newPurchaseMovementsByVesselProductId[productId] ?? []
            let newArray = existing.filter(x => !purchaseMovements.find(y => y.id === x.id)).concat(purchaseMovements)
            newPurchaseMovementsByVesselProductId = { ...newPurchaseMovementsByVesselProductId, [productId]: newArray }
        }
        setPurchaseMovementsByVesselProductId(newPurchaseMovementsByVesselProductId)
    }

    let updateFixedPrice = (vesselProductId: Guid, newFixedPrice: number | null) => {
        let productPurchaseMovement: PurchaseMovement[] = purchaseMovementsByVesselProductId[vesselProductId]

        for (let purchaseMovement of productPurchaseMovement) {
            if (purchaseMovement.type != "FixedPrice") continue

            for (let site of purchaseMovement.sites) {
                site.fixedPrice = newFixedPrice
            }
        }
        setFixedPriceByVesselProductId({ ...fixedPriceByVesselProductId, [vesselProductId]: newFixedPrice })
        setPurchaseMovements(vesselProductId, [...productPurchaseMovement])
    }

    let applyFixedPrice = (vesselProductId: Guid, site: SitePurchaseMovement): SitePurchaseMovement => {
        return { ...site, fixedPrice: fixedPriceByVesselProductId[vesselProductId] }
    }

    let initFixedPrice = (vesselProducts: VesselProduct[]) => {
        let fixedPrices = {}
        for (let vesselProduct of vesselProducts) {
            let fixedPrice = vesselProduct.purchaseMovements.find(x => x.type === "FixedPrice")?.sites[0].fixedPrice ?? null
            fixedPrices[vesselProduct.id] = fixedPrice
        }
        setFixedPriceByVesselProductId(fixedPrices)
    }

    let removePurchaseMovement = (vesselProductId: Guid, purchaseMovementId: Guid) => {
        let existing = purchaseMovementsByVesselProductId
        if (!existing) return
        existing[vesselProductId] = [...existing[vesselProductId].filter(x => x.id != purchaseMovementId)]
        setPurchaseMovementsByVesselProductId(existing)
    }

    let removeSitePurchaseMovement = (sitePurchaseMovementId: Guid, purchaseMovementId: Guid, vesselProductId: Guid) => {
        let existing = purchaseMovementsByVesselProductId
        if (!existing || !existing[vesselProductId]) return
        let purchaseMovement = existing[vesselProductId].find(x => x.id == purchaseMovementId)
        if (!purchaseMovement) return

        if (purchaseMovement.sites.length == 1 && purchaseMovement.sites[0].id == sitePurchaseMovementId)
            removePurchaseMovement(vesselProductId, purchaseMovementId)
        else {
            purchaseMovement.sites = purchaseMovement.sites.filter(x => x.id != sitePurchaseMovementId)
            existing[vesselProductId] = existing[vesselProductId].filter(x => x.id != purchaseMovementId)
            existing[vesselProductId].push(purchaseMovement)
            setPurchaseMovementsByVesselProductId(existing)
        }
    }

    let setCostingInformation = (productId: Guid, costingInformation: CostingInformation) => {
        setCostingInformationByVesselProductId({ ...costingInformationByVesselProductId, [productId]: costingInformation })
    }

    let updateCostingInformations = (vesselProducts: VesselProduct[]) => {
        let costingInfos = costingInformationByVesselProductId
        for (let i = 0; i < vesselProducts.length; i++) {
            costingInfos[vesselProducts[i].id] = vesselProducts[i].costingInformation
        }
        setCostingInformationByVesselProductId({ ...costingInfos })
    }

    let setChoices = (
        vesselUsers: VesselUser[],
        paymentTerm: PaymentTerm[],
        companys: CompanyName) => {
        setVesselUsers(vesselUsers)
        setPaymentTerms(paymentTerm)
        setCompanys(companys)
    }

    let updateVesselProducts = (vesselProducts: VesselProduct[]) => {
        setPurchaseMovementsProducts(vesselProducts.map(p => [p.id, p.purchaseMovements]))
        state?.products.push.apply(state?.products, vesselProducts)
        if (state && state?.products.length > 1)
            state?.products.sort((a, b) => (products[a.productId] > products[b.productId]) ? 1 : -1)
        initFixedPrice(state?.products)
    }

    let isVisible = (company: string, dutyStatus: string) =>
        hasScopeOnClaim(Claims.Companies, company) || hasScopeOnClaim(Claims.VesselPurchaseDelegation, `${company}/${dutyStatus}`)

    let load = async (id: Guid, productId?: Guid) => {
        let vessel = await api.get<Vessel>(`vessel/${id}`)
        setState(vessel)

        vessel.products.forEach(setCanDeleteProduct)
        setUrl(id, productId)
        await loadCounterpartys(vessel.products.flatMap(x => x.purchaseMovements).map(x => x.companyCode).distinct())
        if (vessel)
            setPurchaseMovementsProducts(vessel.products.map(p => [p.id, p.purchaseMovements]))
        updateCostingInformations(vessel.products)
        setCanEdit(vessel.canEdit)
        loadVesselFieldCatalog(vessel.jettyCode)
        loadProducts(vessel.jettyCode)
        loadPricingNeeds(vessel)
        initFixedPrice(vessel.products)
        await loadRegulatedPricePeriods(vessel.jettyCode, vessel.completionOfDischargeDate)
        await updateSites(vessel?.jettyCode)
        await updateCounterpartys(vessel?.jettyCode)
        await getCanCreatePurchaseOrder(id)
        await updatePricingPeriodRules()
    }

    let setCanDeleteProduct = (product: VesselProduct) => {
        product.canRemove = !product.purchaseMovements
            .flatMap(x => x.sites.flatMap(x => [x.volume, x.quantity]).flatMap(x => x))
            .some(x => x ?? 0 > 0)
    }

    let setUrl = (id: Guid, productId?: Guid) => {
        if (id) {
            let urlState = '?openVessel=' + id
            if (productId) {
                urlState += '&product=' + productId
                setActiveProductId(productId)
            }
            else if (activeProductId) {
                urlState += '&product=' + activeProductId
            }
            window.history.pushState({}, '', urlState)
        }
    }

    let loadProducts = async (jetty: string | null) => {
        if (!jetty) return
        let products = await api.get<{ id: string, code: string }[]>(`vessel/product/${jetty}`)
        let productCodes = products.indexValueBy(x => x.id, x => x.code)
        setProducts(productCodes)
    }

    let loadPricingNeeds = async (vessel: Vessel) => {
        let allSitePurchaseMovementIds = vessel.products
            .flatMap(x => x.purchaseMovements)
            .flatMap(x => x.sites)
            .map(x => x.id)

        let allVesselPricingNeedPromises = allSitePurchaseMovementIds.map(x => api.get<PricingNeed>(`vessel/pricingNeed/${x}`))

        Promise.all(allVesselPricingNeedPromises)
            .then(values => setPricingNeeds(
                values.reduce((acc, curr) => {
                    acc[curr.sitePurchaseMovementId] = curr.value
                    return acc
                }, {})))
    }

    let loadRegulatedPricePeriods = async (jetty: string | null, completionOfDischargeDate: string | null) => {
        if (!jetty || !completionOfDischargeDate) return
        let regulatedPricePeriods = await api.get<RegulatedPricePeriod[]>(`vessel/regulatedPricePeriods/${jetty}`, { date: completionOfDischargeDate })
        setRegulatedPricePeriods(regulatedPricePeriods)
    }

    let loadVesselFieldCatalog = async (jetty: string | null) => {
        if (!jetty) return
        let fields = await api.get<string[]>(`vessel/fields/${jetty}`)

        let firstLetterToLower = (str: string): string => str.charAt(0).toLocaleLowerCase() + str.slice(1)

        let formatedFields = fields.map(x => {
            let splittedField = x.split('.')
            return splittedField.length === 2 ? firstLetterToLower(splittedField[1]) : firstLetterToLower(splittedField[0])
        })

        setVesselFieldsCatalog(formatedFields)
    }

    let loadUsers = async (jettyCode: string) => {
        let vesselUsers = await api.get<VesselUser[]>('vessel/users', { jetty: jettyCode })
        setVesselUsers(vesselUsers)
    }

    let loadCounterpartys = async (companys: string[]) => {
        let newTransporters = { ...transporters }
        let newSalesCounterpartys = { ...salesCounterpartys }

        for (let company of companys) {
            let existingTransporters = newTransporters[company]
            let existingSalesCounterpartys = newSalesCounterpartys[company]
            if (!existingTransporters || !existingTransporters.length || !existingSalesCounterpartys || !existingSalesCounterpartys.length) {
                let companyCounterpartys = await api.get<Counterparty[]>(`vessel/${company}/counterparty`)
                if (!existingTransporters || !existingTransporters.length) newTransporters[company] = companyCounterpartys
                if (!existingSalesCounterpartys || !existingSalesCounterpartys.length) {
                    let companySapCounterpartyIds = await api.get<Guid[]>(`vessel/${company}/sapCounterpartyIds`)
                    newSalesCounterpartys[company] = companyCounterpartys.filter(x => companySapCounterpartyIds.includes(x.id))
                }
            }
        }

        setTransporters(newTransporters)
        setSalesCounterpartys(newSalesCounterpartys)
    }

    let isAuthorized = (field: keyof Vessel | keyof VesselProduct | keyof CostingInformation | keyof SitePurchaseMovement | ComplementaryVesselFields) =>
        vesselFieldsCatalog.some(x => x === field)

    let changeJetty = async (jettyCode: string) => {
        if (!state) return
        let jetty = jettys.find(x => x.code === jettyCode)
        let meanOfTransportation = jetty?.defaultMeanOfTransportation ?? 'Ship'
        if (isReferenceIdMasterDataActive && state.meanOfTransportation !== meanOfTransportation)
            formErrors.vesselFieldStatus.setStatus('referenceID', 'none')
        setState({
            ...state,
            jettyCode,
            meanOfTransportation,
            ...(isReferenceIdMasterDataActive && state.meanOfTransportation !== meanOfTransportation
                ? { referenceID: null }
                : {})
        })
    }

    let changeMeanOfTransportation = (meanOfTransportation: VesselMeanOfTransportation) => {
        if (!state) return
        if (isReferenceIdMasterDataActive && state.meanOfTransportation !== meanOfTransportation)
            formErrors.vesselFieldStatus.setStatus('referenceID', 'none')
        setState({
            ...state,
            meanOfTransportation,
            ...(isReferenceIdMasterDataActive && state.meanOfTransportation !== meanOfTransportation
                ? { referenceID: null }
                : {})
        })
    }

    let getCanCreatePurchaseOrder = async (id: Guid) => {
        let poAction = await api.get<{ canCreate: boolean, canEdit: boolean }>(`vessel/purchaseOrderAction?vesselId=${id}`)
        setCanCreatePurchaseOrder(poAction.canCreate)
        setCanEditPurchaseOrder(poAction.canEdit)
    }

    let reload = () => state && load(state.id)

    let ensureReferenceIdPresent = () => {
        if (!formErrors.isReferenceIdMissing()) return true

        formErrors.vesselFieldStatus.setStatus('referenceID', 'alert')
        snackbars.warning(t('warnings.referenceIdMandatory'))
        return false
    }

    let trySave = async () => {
        let newVessel = state
        if (!newVessel || !ensureReferenceIdPresent()) return false

        Object.values(purchaseMovementsByVesselProductId)
            .forEach(x => x.forEach(y => {
                if (y.sites)
                    y.sites = y.sites.filter(f => !!f.siteCode)
            }))

        newVessel.products.forEach(x => {
            x.purchaseMovements = purchaseMovementsByVesselProductId[x.id]
            x.costingInformation = costingInformationByVesselProductId[x.id]
        })

        newVessel.canEdit = false

        try {
            await api.post('vessel', newVessel)
        } catch (err) {
            if (err.status !== 409) throw err
        }
        reload()
        return true
    }

    let open = async (id?: Guid | null, productId?: Guid) => {
        await init()
        setState(initialState)
        dialog.setIsOpen(true)
        id = id ?? guid.createNew() as Guid
        load(id, productId)
    }

    let close = () => {
        dialog.setIsOpen(false)
        setCanEdit(false)
        setState(initialState)
        setVesselFieldsCatalog([])
        window.history.pushState({}, document.title, window.location.pathname)
        setActiveProductId(null)
        setOpenAssignDealPopup(false)
        setDealIdToAssign(null)
        setIsConfirmDealOpen(false)
    }

    let init = async () => {
        setJettys(await api.get<Jetty[]>('vessel/jetty'))
        let companysPromise = api.get<{ code: string, name: string }[]>('vessel/company')
        let paymentTerms = api.get<PaymentTerm[]>('vessel/paymentTerm')
        let users = state?.jettyCode ? await api.get<VesselUser[]>('vessel/users', { jetty: state?.jettyCode }) : []
        let companyCodes = (await companysPromise).indexValueBy(x => x.code, x => x.name)

        setChoices(users, await paymentTerms, companyCodes)
    }

    let deletePurchaseMovement = async (purchaseMovementId: Guid, productId: Guid) => {
        await api.post('vessel/purchaseMovement/delete', { vesselId: state.id, purchaseMovementId: purchaseMovementId })
        removePurchaseMovement(productId, purchaseMovementId)
    }

    let deleteSitePurchaseMovement = async (sitePurchaseMovementId: Guid, purchaseMovementId: Guid, productId: Guid) => {
        let purchaseMovement = purchaseMovementsByVesselProductId[productId].find(x => x.id == purchaseMovementId)
        if (purchaseMovement == null) return
        if (purchaseMovement.sites.length == 1 && purchaseMovement.sites[0].id == sitePurchaseMovementId)
            await deletePurchaseMovement(purchaseMovementId, productId)
        else {
            await api.post('vessel/purchaseMovement/site/delete', { vesselId: state.id, sitePurchaseMovementId: sitePurchaseMovementId })
            removeSitePurchaseMovement(sitePurchaseMovementId, purchaseMovementId, productId)
        }
    }

    let updateSitePurchaseMovementWithAssignedDeal = async (sitePurchaseMovementId: Guid, purchaseMovementId: Guid, productId: Guid) => {
        let purchaseMovement = purchaseMovementsByVesselProductId[productId].find(x => x.id == purchaseMovementId)
        if (purchaseMovement == null) return
        purchaseMovement.sites.forEach(site => {
            if (site.id === sitePurchaseMovementId) {
                site.dealId = dealIdToAssign
            }
        })
    }

    let removeProduct = (product: VesselProduct) => setState({ ...state, products: state.products.filter(x => x !== product) })

    let openAssignableDeals = async (movementId: string) => {
        let assignableDeals = hasDealClaim()
            ? await api.get<Deal[]>(`stock/movement/${movementId}/deal/assignable`)
            : []

        if (assignableDeals.length === 0)
            snackbars.info(t('stock.label.movement.noAssignableDealFound'))
        else {
            setAssignableDealsFromMovement(assignableDeals)
            setOpenAssignDealPopup(true)
        }
    }

    let assignDealFromMovement = async () => {
        if (!ensureReferenceIdPresent()) return
        let { sitePurchaseMovementId, purchaseMovementId, productId } = sitePurchaseMovementWithAssignedDeal ?? {}
        await updateSitePurchaseMovementWithAssignedDeal(sitePurchaseMovementId!, purchaseMovementId!, productId!)
        let purchaseMovement = purchaseMovementsByVesselProductId[productId!].find(x => x.id == purchaseMovementId)
        if (purchaseMovement == null) return
        let site = purchaseMovement.sites.find(x => x.id === sitePurchaseMovementId)
        if (!site) return
        let movementId = site.movementId
        await dealApi.LinkMovement(dealIdToAssign, movementId)
        if (await trySave()) snackbars.success(t('httpSuccess.dealAssigned'))
    }

    let updatePriceIndexs = async (productId: string) => {
        let priceIndexsPromise = api.get<PriceIndex[]>(`vessel/quotation/${state?.jettyCode}/${productId}`)
        setPriceIndexs(await priceIndexsPromise)
    }

    let updatePricingDensitys = async (product: VesselProduct) => {
        let pricingDensity = await api.get<PricingDensitysWithDefault>(`vessel/defaultPricingDensity/${state?.jettyCode}/${product.productId}`)
        if (!product.pricingDensity && !!pricingDensity.defaultPricingDensity) {
            product.pricingDensity = pricingDensity.defaultPricingDensity
            changeProduct(product)
        }
        setPricingDensitys(pricingDensity)
    }

    let updatePricingPeriodRules = async () => {
        let pricingPeriodRulesPromise = api.get<PricingPeriodRule[]>(`vessel/pricingPeriodRules`)
        setPricingPeriodRules(await pricingPeriodRulesPromise)
    }

    let createPurchaseOrder = async () => {
        await api.post('sapApi/multiItemPurchaseOrderFromVessel', { vesselId: state.id })
        reload()
    }

    dialogRef = useRef<OpenCloseDialogRef>(null)
    useImperativeHandle(dialogRef, () => ({ open, close }))

    let activateAdditionalPremium = hasFeature('AdditionalPremiumUnit') && hasFeature('VesselAutoCreateDeal')
        && hasFeature('GenericPricing') && hasFeature('DealQuantityCurrencyUnitPrice')

    let updateSitePurchaseMovementDealId = async (sitePurchaseMovementId: Guid, purchaseMovementId: Guid, productId: Guid, dealId: Guid | null) => {
        let purchaseMovement = purchaseMovementsByVesselProductId[productId].find(x => x.id == purchaseMovementId)
        if (purchaseMovement == null) return
        purchaseMovement.sites.find(site => site.id === sitePurchaseMovementId)!.dealId = dealId
    }

    let unassignDeal = async () => {
        if (!ensureReferenceIdPresent()) return
        let { dealId, movementId } = unassignDealContext
        if (!dealId || !movementId) return
        let { sitePurchaseMovementId, purchaseMovementId, productId } = sitePurchaseMovementWithAssignedDeal ?? {}
        updateSitePurchaseMovementDealId(sitePurchaseMovementId!, purchaseMovementId!, productId!, null)
        await dealApi.unassignMovement(dealId, movementId)
        setUnassignDealContext({ dealId: null, movementId: null })
        setSitePurchaseMovementWithAssignedDeal(null)
        if (await trySave()) snackbars.success(t('httpSuccess.dealUnassigned'))
    }

    return {
        state, canEdit, reload, setState, changeProduct, vesselUsers, counterpartys, sites, jettys, priceIndexs,
        referenceVehicles,
        addProducts, getProductCodeProductIds, addAllPurchaseMovements, updateProductVolume, updatePurchaseMovementVolume,
        companys, paymentTerms, products, purchaseMovementsByVesselProductId,
        setPurchaseMovement, setPurchaseMovements, setPurchaseMovementsByVesselProductId, updateVesselProducts,
        costingInformationByVesselProductId, setCostingInformation, updateCostingInformations,
        movementStock, deletePurchaseMovement, deleteSitePurchaseMovement, updateFixedPrice, applyFixedPrice,
        fixedPriceByVesselProductId, activeProductId, setActiveProductId, isAuthorized, pricingNeeds,
        isVisible, regulatedPricePeriods, removeProduct, transporters, loadCounterpartys, changeJetty,
        changeMeanOfTransportation, changeCounterparty,
        changePurchaseMovementCounterparty,
        openAssignableDeals, openAssignDealPopup, assignableDealsFromMovement, setOpenAssignDealPopup,
        dealIdToAssign, setDealIdToAssign, assignDealFromMovement, isConfirmDealOpen, setIsConfirmDealOpen,
        trySave, updateSitePurchaseMovementWithAssignedDeal, sitePurchaseMovementWithAssignedDeal,
        setSitePurchaseMovementWithAssignedDeal, setPriceIndexs, updatePriceIndexs, formErrors, activateAdditionalPremium,
        updatePricingDensitys, pricingDensitys, canCreatePurchaseOrder, createPurchaseOrder, canEditPurchaseOrder, salesCounterpartys,
        quotationCalendars, pricingPeriodRules, unassignDealConfirmDialogOpen, setUnassignDealConfirmDialogOpen, setUnassignDealContext,
        unassignDeal
    }
}

function useVesselEditDialog() {
    let [isOpen, setIsOpen] = useState<boolean>(false)
    let previousIsOpen = usePrevious(isOpen)
    return { isOpen, setIsOpen, previousIsOpen }
}

export let VesselEditContainer = createContainer(useVesselEdit)
export let VesselEditDialogContainer = createContainer(useVesselEditDialog)